import { Button, Tag, Tooltip } from "antd";
import React from "react";
import { EditIcon, SplitIcon, AlertIcon, ACTION_HOVER } from "../../components/ActionIcons";
import { FieldLabels, Permissions, getPacsStatusLabel, getPatientIdLabel } from "../../constants/fieldLabels";
import { hasPermission } from "../../utils/helper";
// Shared across every table's Status column — never fork a local copy.
import { getStatusBadgeStyle as getStatusStyle } from "../../utils/statusBadgeStyle";

// Shared 2-line clamp style for text cells (wrap once, then ellipsis), paired
// with a Tooltip for the full value. Matches the Orders/PACS/Worklist tables'
// clamp style rather than single-line nowrap+ellipsis truncation.
const ellipsisCell: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  width: '100%',
  lineHeight: 1.2,
};


export const orderColumns = ({
  onEditStudyDetails,
  onSplitStudy,
}: any = {}) => [
  {
    dataIndex: 'po_pin',
    key: 'po_pin',
    title: getPatientIdLabel(),
    width: 105,
    render: (text, rec) => {
      const patPin = rec?.patient?.pat_pin;
      return (
        <Tooltip title={patPin}>
          <div style={ellipsisCell}>{patPin}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_name',
    title: FieldLabels.PATIENT_NAME,
    render: (text, record, openReportEditor, openViewer) => {
      const isEmergency = record.po_priority?.toUpperCase() === 'EMERGENCY';
      const patientName = record.patient?.pat_name;

      return (
        <div>
          <Button
            className="ms-1auto d-flex align-items-center ph-0 col-pat-name"
            type="link"
            style={{ padding: 0 }}
          >
            <Tooltip title={patientName}>
              <span
                style={{
                  ...ellipsisCell,
                  maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%',
                }}
              >
                {patientName}
              </span>
            </Tooltip>
            {isEmergency && (
              <Tooltip title="Emergency Case">
                <span style={{ display: 'inline-flex', marginLeft: '6px', animation: 'pulse 2s ease-in-out infinite' }}>
                  <AlertIcon size={16} />
                </span>
              </Tooltip>
            )}
          </Button>
        </div>
      );
    },
    width: 250,
  },
  {
    dataIndex: 'modality',
    key: 'modality',
    title: FieldLabels.MODALITY,
    width: 100,
    render: (text, rec) => (
      <span
        style={{
          background: '#F5F5F5',
          border: '1px solid #EAEEF2',
          height: '24px',
          borderRadius: '4px',
          padding: '2px 8px',
          display: 'inline-block',
          lineHeight: '20px',
        }}
      >
        {rec?.po_modality}
      </span>
    ),
  },
  {
    dataIndex: 'po_body_part',
    title: FieldLabels.BODY_PART,
    width: 120,
    render: (text, rec) => rec?.po_body_part,
  },
  {
    dataIndex: 'po_diag_desc',
    title: FieldLabels.STUDY_DESCRIPTION_SHORT,
    width: 260,
    render: (txt, rec) => {
      return (
        <Tooltip title={rec?.po_priority}>
          <span style={ellipsisCell}>{rec?.po_diag_desc}</span>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_ord_no',
    title: FieldLabels.ORDER_NUMBER,
    width: 100,
    render: (text, rec) => rec?.po_his_ord_no,
  },
  {
    dataIndex: 'po_acc_no',
    key: 'po_acc_no',
    title: FieldLabels.ACCESSION_NUMBER,
    width: 120,
    render: (text, rec) => {
      const accNo = rec?.po_acc_no;
      return (
        <Tooltip title={accNo}>
          <div style={ellipsisCell}>{accNo}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'ps_study_uid',
    title: 'Study UID',
    width: 260,
    render: (text, rec) => rec?.pacs_study?.ps_study_uid || '',
  },
  // {
  //   dataIndex: 'ps_series_count',
  //   title: 'Series Count',
  //   width: 120,
  //   align: 'right',
  //   render: (text, rec) => (
  //     <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>
  //       {rec?.pacs_study?.ps_series_count || ''}
  //     </span>
  //   ),
  // },
  {
    dataIndex: 'po_ref_doc',
    title: FieldLabels.REFERRING_DOCTOR,
    width: 182,
    render: (text, rec) => {
      const refDoc = rec?.po_ref_doc;
      return (
        <Tooltip title={refDoc}>
          <div style={ellipsisCell}>{refDoc}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_status',
    title: FieldLabels.STATUS,
    fixed: 'right',
    // Wide enough for the shared badge's widest label ("UNREPORTED").
    width: 124,
    render: (text, record) => {
      const pacs_status = record?.order_workflow?.ow_pacs_status;
      return (
        <Tag
          className="custom-status-tag"
          style={getStatusStyle(pacs_status)}
        >
          {getPacsStatusLabel(pacs_status)}
        </Tag>
      );
    },
  },
  {
    dataIndex: 'actions',
    title: 'Actions',
    width: 90,
    fixed: 'right',
    render: (text, record) => {
      const studyUID = record?.pacs_study?.ps_study_uid;
      const pacsStatus = record?.order_workflow?.ow_pacs_status?.toUpperCase();
      const isEditableStatus = pacsStatus === 'SCANNED';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* DICOM Edit requires MODIFY_STUDY_DETAILS (also enforced server-side
              on /update-study-details). */}
          {hasPermission(Permissions.MODIFY_STUDY_DETAILS) && (
            <Tooltip title="DICOM Edit">
              <span
                className="action-icon-button"
                style={{ ['--ai-hover' as any]: ACTION_HOVER.edit }}
                onClick={e => {
                  e.stopPropagation();
                  onEditStudyDetails?.(record);
                }}
              >
                <EditIcon size={18} />
              </span>
            </Tooltip>
          )}
          {studyUID && isEditableStatus && (
            <Tooltip title="Split Study">
              <span
                className="action-icon-button"
                style={{ ['--ai-hover' as any]: ACTION_HOVER.split }}
                onClick={e => {
                  e.stopPropagation();
                  onSplitStudy?.(record);
                }}
              >
                <SplitIcon size={18} />
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
  },
];


// SavedSearches is now a single shared implementation (neutral-pill shortcut
// row). Re-exported from the orders page to avoid a duplicate copy here.
export { SavedSearches } from '../orders/constants';

// Old QuickFilters component - COMMENTED OUT - Now using SavedSearches component for quick filters
// This component was at wrong placement, replaced with dynamic quick filters in SavedSearches component
// export const QuickFilters = ({ quickFilters, handleQuickFilters }) => {
//   return (
//     <div className="mb-2">
//       {quickFilters.map((filter, index) => (
//         <span className="quick-filters ml-3" key={filter.qf_name} onClick={() => handleQuickFilters(filter)}>
//           {filter.qf_name}
//         </span>
//       ))}
//     </div>
//   )
// }
